import { CoherencePanel } from '@coherence-design-system/controls';
// import { EventType, UserEvent } from '@employee-experience/common';
import { Context } from '@micro-frontend-react/employee-experience/lib/Context';
import { IEmployeeExperienceContext } from '@micro-frontend-react/employee-experience/lib/IEmployeeExperienceContext';
import { DefaultButton } from '@micro-frontend-react/employee-experience/lib/Buttons/DefaultButton';
import { getFeature } from '@micro-frontend-react/employee-experience/lib/UsageTelemetryHelper';
import { Spinner } from 'office-ui-fabric-react';
import { PrimaryButton } from 'office-ui-fabric-react/lib/components/Button';
import * as React from 'react';
import { useRef, useState } from 'react';
import { Document, Page as PdfPage, pdfjs } from 'react-pdf';
import { ILetterTemplate } from '../../../models/ILetterTemplate';
import { requestLetterSubmission } from '../../../redux/actions/metadataActions';
import { useLetterMetadata } from '../../../redux/hooks/useLetterMetadata';
import { useUserProfile } from '../../../redux/hooks/userUserProfile';
import { MetadataContext } from '../../../shared/MetadataContext';
import { PageContainer } from '../../Home.styled';
import * as styled from './PdfPanel.styled';
import { Link } from '../../Share.styled';
import { EventType, UserEvent } from '@micro-frontend-react/employee-experience/lib/UsageTelemetry';

export interface IFile {
  blob: Blob;
  url: string;
}

const b64toBlob = (b64Data: string, contentType = '', sliceSize = 512) => {
  const byteCharacters = atob(b64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, { type: contentType });
  return blob;
};

export interface IPdfPanelProps {
  selectedLetter: ILetterTemplate;
}

// export const MetadataPanel = React.memo(function MetadataPanelContent(
//     props: IMetadataPanelProps
// ): React.ReactElement {

export const PdfPanel = React.memo(function PdfPanelContent(
  props: IPdfPanelProps
): React.ReactElement {
  const { selectedLetter } = props;
  const { telemetryClient } = React.useContext(
    Context as React.Context<IEmployeeExperienceContext>
  );
  const { useSelector, dispatch } = React.useContext(
    Context as React.Context<IEmployeeExperienceContext>
  );
  const [isCalloutVisible, setCalloutVisibility] = React.useState(true);
  const feature = getFeature('EVL', 'PdfPanel');

  const [file, setFile] = useState<IFile>({
    url: '',
    blob: null,
  });
  const [numPages, setNumPages] = useState(null);
  const [
    letterMetadata,
    letterPdf,
    hasError,
    isLoading,
    errorMessage,
    isLoadingLetterGeneration,
    isLoadingLetterPreview,
    hasErrorLetterGeneration,
  ] = useLetterMetadata(feature, selectedLetter);

  const generatePreview = () => {
    const b64Data = letterPdf.pdfLetter;
    const blob = b64toBlob(b64Data, 'application/pdf');
    const url = window.URL.createObjectURL(new Blob([blob]));
    pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;    
    setFile({
      url: url,
      blob: blob,
    });
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  const messagesEndRef = useRef(null);

  React.useEffect(() => {
    if (letterPdf != null && letterPdf.pdfLetter != '') {
      generatePreview();
    }
  }, [letterPdf]);

  return (
    <>
      <MetadataContext.Consumer>
        {(context) => (
          <CoherencePanel
            isOpen={context.showPdfPanel}
            onDismiss={() => {
              if (isLoadingLetterGeneration) return;
              context.togglePdfPanel(false);
              context.toggleMetadataPanel(false);
            }}
            customWidth={`900px`}
            isLightDismiss={true}
            titleText={`Letter Preview: ${selectedLetter?.name}`}
            closeButtonAriaLabel="Close panel"
            hasCloseButton={true}
            backButton={{
              text: 'Go back',
              onClick: () => {
                if (isLoadingLetterGeneration) return;
                context.togglePdfPanel(false);
                context.toggleMetadataPanel(true);
              },
            }}
            overlayProps={{
              isDarkThemed: true,
            }}
            telemetryHook={telemetryClient}
            styles={{
              subComponentStyles: {
                panelStyles: {
                  scrollableContent: {
                    width: 'fit-content',
                    minWidth: '100%',
                  },
                },
              },
            }}
          >
            <p>
              Please check if the information is accurate. If necessary, submit{' '}
              <Link
                to={
                  context.userProfile != null && context.userProfile.allowedAva
                    ? __Ava_Link__
                    : __AskHR_Application__Link__
                }
                title="AskHR Link"
                target="_blank"
                usageEvent={{
                  feature: 'EVL',
                  subFeature: 'PdfPanel.AskHRLink',
                }}
              >
                AskHR
              </Link>{' '}
              ticket on an issue in context of the letter.
            </p>
            <styled.Separator />
            <PageContainer>
              {isLoadingLetterGeneration &&
                !hasErrorLetterGeneration &&
                !selectedLetter?.requiresManualProcessing && (
                  <Spinner label="Generating letter... Please do not close the browser." />
                )}
              {isLoadingLetterGeneration &&
                !hasErrorLetterGeneration &&
                selectedLetter?.requiresManualProcessing && (
                  <Spinner label="Generating preview... Please do not close the browser." />
                )}
              {!isLoadingLetterGeneration && hasErrorLetterGeneration && (
                <styled.SuccessGenerationContainer>
                  <styled.SuccessImage src={`./assets/NotFoundLogo.png`} role="presentation" />
                  <styled.BreakFlexDiv />
                  <styled.SuccessMessage>
                    Oops! Something went wrong while generating the letter.
                  </styled.SuccessMessage>
                  <styled.BreakFlexDiv />
                  <DefaultButton
                    text="Try again"
                    title="Try again"
                    usageEvent={{
                      feature: 'EVL',
                      subFeature: 'PdfPanel.TryAgainLink',
                    }}
                    style={{ marginTop: 20 }}
                    onClick={() => {
                      context.togglePdfPanel(false);
                    }}
                  />
                </styled.SuccessGenerationContainer>
              )}
              {!isLoadingLetterGeneration && !hasErrorLetterGeneration && (
                <>
                  <styled.ButtonsContainer>
                    <PrimaryButton
                      id={'btnSubmitDownload'}
                      name={'btnSubmitDownload'}
                      text={selectedLetter?.requiresManualProcessing ? 'Submit' : 'Download'}
                      title={selectedLetter?.requiresManualProcessing ? 'Submit' : 'Download'}
                      onClick={() => {
                        context.togglePdfPanel(false);

                        if (!selectedLetter?.requiresManualProcessing) {
                          context.toggleSuccessGenerationPanel(true);
                          const link = document.createElement('a');
                          link.href = file.url;
                          link.setAttribute('download', `${letterPdf?.filename}`);
                          document.body.appendChild(link);
                          link.click();
                          link.parentNode.removeChild(link);
                          window.URL.revokeObjectURL(file?.url);
                          setFile({ url: '', blob: null });

                          const linkEvent: UserEvent = {
                            feature: 'EVL',
                            subFeature: 'Home.PdfPreview.DownloadSubmitButton',
                            subFeatureLevel2: '',
                            featureLocation: 'Home',
                            eventName: 'ManualLetterSubmitted',
                            type: EventType.User,
                          };

                          telemetryClient.trackEvent(linkEvent, {
                            SelectedLetterId: selectedLetter?.id,
                            SelectedLetterName: selectedLetter?.name,
                          });
                        } else {
                          dispatch(requestLetterSubmission(feature, context.submissionRequest));
                          if (selectedLetter?.isSoftCopy)
                            context.toggleEmailedGenerationPanel(true);
                          else context.toggleMailedGenerationPanel(true);

                          const linkEvent: UserEvent = {
                            feature: 'EVL',
                            subFeature: 'Home.PdfPreview.DownloadSubmitButton',
                            subFeatureLevel2: '',
                            featureLocation: 'Home',
                            eventName: 'AutomatedLetterDownloaded',
                            type: EventType.User,
                          };

                          telemetryClient.trackEvent(linkEvent, {
                            SelectedLetterId: selectedLetter?.id,
                            SelectedLetterName: selectedLetter?.name,
                          });
                        }
                      }}
                      style={{ marginRight: 10 }}
                    />
                    <div ref={messagesEndRef} />
                  </styled.ButtonsContainer>
                  <styled.DocumentContainer>
                    <Document
                      className={'documentContainerBorderless'}
                      file={file?.url}
                      onLoadSuccess={onDocumentLoadSuccess}
                    >
                      {Array.from({ length: numPages }).map((_, page) => (
                        <>
                          <styled.PdfPageCounter>
                            Page {page + 1} of {numPages}
                          </styled.PdfPageCounter>
                          <styled.PdfPageBordered>
                            <PdfPage width={350} height={998} scale={1.8} pageNumber={page + 1} renderTextLayer={false} />
                          </styled.PdfPageBordered>
                        </>
                      ))}
                    </Document>
                  </styled.DocumentContainer>
                </>
              )}
            </PageContainer>
          </CoherencePanel>
        )}
      </MetadataContext.Consumer>
    </>
  );
});
